// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useRef } from 'react';

interface FileUploadFieldProps {
  fieldKey: string;
  label?: string;
  files?: File[];
  onAddFile?: (fieldKey: string, file: File) => void;
  onRemoveFile?: (fieldKey: string, index?: number) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const fileIcon = (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_file_icon)">
      <mask id="mask0_file_icon" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
        <path d="M32 0H0V32H32V0Z" fill="white"/>
      </mask>
      <g mask="url(#mask0_file_icon)">
        <path d="M19.9993 30.3332H11.9993C4.75935 30.3332 1.66602 27.2398 1.66602 19.9998V11.9998C1.66602 4.75984 4.75935 1.6665 11.9993 1.6665H18.666C19.2127 1.6665 19.666 2.11984 19.666 2.6665C19.666 3.21317 19.2127 3.6665 18.666 3.6665H11.9993C5.85268 3.6665 3.66602 5.85317 3.66602 11.9998V19.9998C3.66602 26.1465 5.85268 28.3332 11.9993 28.3332H19.9993C26.146 28.3332 28.3327 26.1465 28.3327 19.9998V13.3332C28.3327 12.7865 28.786 12.3332 29.3327 12.3332C29.8794 12.3332 30.3327 12.7865 30.3327 13.3332V19.9998C30.3327 27.2398 27.2393 30.3332 19.9993 30.3332Z" fill="#E7000B"/>
        <path d="M29.3327 14.3337H23.9993C19.4393 14.3337 17.666 12.5603 17.666 8.00031V2.66698C17.666 2.26698 17.906 1.89364 18.2793 1.74698C18.6527 1.58698 19.0793 1.68031 19.3727 1.96031L30.0394 12.627C30.3194 12.907 30.4127 13.347 30.2527 13.7203C30.0927 14.0937 29.7327 14.3337 29.3327 14.3337ZM19.666 5.08031V8.00031C19.666 11.4403 20.5593 12.3336 23.9993 12.3336H26.9193L19.666 5.08031Z" fill="#E7000B"/>
      </g>
    </g>
    <defs>
      <clipPath id="clip0_file_icon">
        <rect width="32" height="32" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const removeIcon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_remove_icon)">
      <mask id="mask0_remove_icon" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
        <path d="M24 0H0V24H24V0Z" fill="white"/>
      </mask>
      <g mask="url(#mask0_remove_icon)">
        <path d="M12 22.75C6.07 22.75 1.25 17.93 1.25 12C1.25 6.07 6.07 1.25 12 1.25C17.93 1.25 22.75 6.07 22.75 12C22.75 17.93 17.93 22.75 12 22.75ZM12 2.75C6.9 2.75 2.75 6.9 2.75 12C2.75 17.1 6.9 21.25 12 21.25C17.1 21.25 21.25 17.1 21.25 12C21.25 6.9 17.1 2.75 12 2.75Z" fill="#141414"/>
        <path d="M9.16937 15.5794C8.97937 15.5794 8.78938 15.5094 8.63938 15.3594C8.34938 15.0694 8.34938 14.5894 8.63938 14.2994L14.2994 8.63938C14.5894 8.34938 15.0694 8.34938 15.3594 8.63938C15.6494 8.92937 15.6494 9.40936 15.3594 9.69936L9.69938 15.3594C9.55938 15.5094 9.35937 15.5794 9.16937 15.5794Z" fill="#141414"/>
        <path d="M14.8294 15.5794C14.6394 15.5794 14.4494 15.5094 14.2994 15.3594L8.63938 9.69936C8.34938 9.40936 8.34938 8.92937 8.63938 8.63938C8.92937 8.34938 9.40938 8.34938 9.69938 8.63938L15.3594 14.2994C15.6494 14.5894 15.6494 15.0694 15.3594 15.3594C15.2094 15.5094 15.0194 15.5794 14.8294 15.5794Z" fill="#141414"/>
      </g>
    </g>
    <defs>
      <clipPath id="clip0_remove_icon">
        <rect width="24" height="24" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

export function FileUploadField({
  fieldKey,
  label = 'Upload Documents',
  files,
  onAddFile,
  onRemoveFile,
}: FileUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-col gap-sm">
      <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
        {label}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.xls,.xlsx,.doc,.docx,.csv,.png,.jpg,.jpeg"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onAddFile?.(fieldKey, f);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="h-[48px] px-lg w-fit border border-purple-primary rounded-button bg-white hover:bg-lilac-05 transition-colors cursor-pointer"
      >
        <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
          Upload Document
        </div>
      </button>

      <div className="text-grey-04 font-sans text-body-03 font-normal leading-normal">
        Accepted file type: PDF, Word, Excel, JPG, PNG
      </div>

      {files && files.length > 0 && (
        <div className="flex flex-col gap-sm">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between px-lg py-md rounded-lg border border-[#A3A3A3] bg-white self-stretch"
            >
              <div className="flex items-center gap-sm">
                <div className="shrink-0">{fileIcon}</div>
                <div className="flex flex-col gap-[2px]">
                  <div className="text-black font-sans text-body-03 font-medium leading-normal">
                    {file.name}
                  </div>
                  <div className="text-grey-04 font-sans text-body-04 font-normal leading-normal">
                    {formatFileSize(file.size)}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRemoveFile?.(fieldKey, index)}
                className="shrink-0 cursor-pointer bg-transparent border-none p-0 flex items-center"
                aria-label="Remove file"
              >
                {removeIcon}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
